import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffRounded'
import RefreshIcon from '@mui/icons-material/RefreshRounded'
import RestartAltIcon from '@mui/icons-material/RestartAltRounded'
import WarningAmberIcon from '@mui/icons-material/WarningAmberRounded'
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadIcon from '@mui/icons-material/DownloadRounded'
import HistoryIcon from '@mui/icons-material/HistoryRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import type { SelectChangeEvent } from '@mui/material/Select'
import { useOutletContext } from 'react-router-dom'
import { usePageTitle } from '../../components/common/PageTitleContext'
import type { AppOutletContext } from '../../app/AppShell'
import {
  getSuperadminWeatherSettings,
  getSuperadminWeatherReports,
  getSuperadminWeatherReportRaw,
  getSuperadminWeatherReportRawUrl,
  refreshWeather,
  resetSuperadminWeatherCounters,
  saveSuperadminWeatherSettings,
  type SaveSuperadminWeatherSettingsPayload,
  type SuperadminWeatherSettingsResponse,
  type SuperadminWeatherRateLimit,
  type WeatherReportListEntry,
  type WeatherReportRawPayload,
} from '../../api/weather'

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => index)
const HISTORY_RANGE_PRESETS: Array<{ value: '24h' | '48h' | '7d'; label: string }> = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '48h', label: 'Last 48 hours' },
  { value: '7d', label: 'Last 7 days' },
]
const WEATHER_REFRESH_TYPES: Array<{ value: string; label: string }> = [
  { value: '', label: 'All refresh types' },
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
  { value: 'cron', label: 'Cron' },
  { value: 'adhoc', label: 'Ad hoc' },
]

type FormState = {
  apiKey: string
  apiKeyDirty: boolean
  hasStoredApiKey: boolean
  manualCooldown: string
  cronType: 'interval_minutes' | 'specific_hours'
  intervalMinutes: string
  specificHours: number[]
  presenterManualRefreshEnabled: boolean
}

type FormErrors = {
  specificHours?: string
}

type NoticeState = {
  type: 'success' | 'error' | 'info'
  message: string
}

type WeatherHistoryFilters = {
  stationId: number | null
  status: 'all' | 'success' | 'error'
  range: '24h' | '48h' | '7d'
  start: string
  end: string
  refreshType: string | null
  page: number
  perPage: number
}

type WeatherHistoryState = {
  rows: WeatherReportListEntry[]
  total: number
  hasNext: boolean
}

type RawDialogState = {
  open: boolean
  reportId: number | null
  loading: boolean
  payload: WeatherReportRawPayload | null
  error: string | null
  copied: boolean
}

const createDefaultFormState = (): FormState => ({
  apiKey: '',
  apiKeyDirty: false,
  hasStoredApiKey: false,
  manualCooldown: '0',
  cronType: 'interval_minutes',
  intervalMinutes: '60',
  specificHours: [],
  presenterManualRefreshEnabled: false,
})

const createDefaultHistoryFilters = (stationId: number | null): WeatherHistoryFilters => ({
  stationId,
  status: 'all',
  range: '24h',
  start: '',
  end: '',
  refreshType: null,
  page: 1,
  perPage: 25,
})

const sanitizeHours = (hours: number[]): number[] =>
  Array.from(new Set(hours.filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23))).sort(
    (a, b) => a - b,
  )

const buildFormStateFromResponse = (response: SuperadminWeatherSettingsResponse): FormState => {
  const hasStoredApiKey = response.api_key === '__REDACTED__'
  const cronMode = response.cron_mode ?? {}
  const cronType = cronMode.type === 'specific_hours' ? 'specific_hours' : 'interval_minutes'
  const intervalNumberRaw =
    typeof cronMode.interval_minutes === 'number'
      ? cronMode.interval_minutes
      : Number.parseInt(String(cronMode.interval_minutes ?? '60'), 10)
  const intervalNumber = Number.isFinite(intervalNumberRaw) ? Math.max(1, intervalNumberRaw) : 60
  const manualCooldownRaw =
    typeof response.manual_cooldown_minutes === 'number'
      ? response.manual_cooldown_minutes
      : Number.parseInt(String(response.manual_cooldown_minutes ?? '0'), 10)
  const manualCooldown = Number.isFinite(manualCooldownRaw) ? Math.max(0, manualCooldownRaw) : 0
  const specificHoursRaw = Array.isArray(cronMode.specific_hours)
    ? cronMode.specific_hours.map((value) => Number(value))
    : []

  return {
    apiKey: hasStoredApiKey ? '' : typeof response.api_key === 'string' ? response.api_key : '',
    apiKeyDirty: false,
    hasStoredApiKey,
    manualCooldown: String(manualCooldown),
    cronType,
    intervalMinutes: String(intervalNumber),
    specificHours: sanitizeHours(specificHoursRaw),
    presenterManualRefreshEnabled: Boolean(response.presenter_manual_refresh_enabled),
  }
}

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '—'
  }
  const timestamp = Date.parse(value)
  if (Number.isNaN(timestamp)) {
    return value
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error) {
    return err.message || fallback
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message
    if (typeof message === 'string' && message.trim() !== '') {
      return message
    }
  }
  return fallback
}

const formatDuration = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
    return '—'
  }

  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} s`
  }

  return `${value} ms`
}

const clampDateValue = (value: string | null | undefined): string => (typeof value === 'string' ? value.slice(0, 10) : '')

const rateLimitLabels: Record<string, string> = {
  minute: 'Per minute',
  day: 'Per day',
}

const GlobalWeatherSettingsPage: React.FC = () => {
  const appContext = useOutletContext<AppOutletContext>()
  const { activeStation, me, includeSuperadmin } = appContext
  const stationId = activeStation?.id ?? null
  const stations = React.useMemo(() => me?.stations ?? [], [me?.stations])
  const isSuperadmin = Boolean(includeSuperadmin)

  usePageTitle('Global weather settings')

  const [form, setForm] = React.useState<FormState>(() => createDefaultFormState())
  const [formErrors, setFormErrors] = React.useState<FormErrors>({})
  const [settings, setSettings] = React.useState<SuperadminWeatherSettingsResponse | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [testing, setTesting] = React.useState(false)
  const [resetting, setResetting] = React.useState(false)
  const [notice, setNotice] = React.useState<NoticeState | null>(null)
  const [showApiKey, setShowApiKey] = React.useState(false)
  const [historyFilters, setHistoryFilters] = React.useState<WeatherHistoryFilters>(() =>
    createDefaultHistoryFilters(stationId),
  )
  const [historyState, setHistoryState] = React.useState<WeatherHistoryState>({
    rows: [],
    total: 0,
    hasNext: false,
  })
  const [historyLoading, setHistoryLoading] = React.useState(false)
  const [historyError, setHistoryError] = React.useState<string | null>(null)
  const [historyReloadKey, setHistoryReloadKey] = React.useState(0)
  const [rawDialog, setRawDialog] = React.useState<RawDialogState>({
    open: false,
    reportId: null,
    loading: false,
    payload: null,
    error: null,
    copied: false,
  })
  const previousStationIdRef = React.useRef<number | null>(stationId)

  React.useEffect(() => {
    if (stationId === previousStationIdRef.current) {
      return
    }

    setHistoryFilters((prev) => {
      if (prev.stationId !== previousStationIdRef.current) {
        previousStationIdRef.current = stationId
        return prev
      }

      previousStationIdRef.current = stationId
      return {
        ...prev,
        stationId,
        page: 1,
      }
    })
  }, [stationId])

  const applySettings = React.useCallback((response: SuperadminWeatherSettingsResponse) => {
    setSettings(response)
    setForm(buildFormStateFromResponse(response))
  }, [])

  const loadSettings = React.useCallback(async () => {
    if (!stationId) {
      setSettings(null)
      setForm(createDefaultFormState())
      setLoadError(null)
      return
    }

    setLoading(true)
    setLoadError(null)

    try {
      const response = await getSuperadminWeatherSettings(stationId)
      applySettings(response)
    } catch (error) {
      setLoadError(getErrorMessage(error, 'Unable to load global weather settings.'))
      setSettings(null)
    } finally {
      setLoading(false)
    }
  }, [applySettings, stationId])

  React.useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  React.useEffect(() => {
    if (!isSuperadmin) {
      return
    }

    const controller = new AbortController()
    setHistoryLoading(true)
    setHistoryError(null)

    const payload: WeatherReportListFilters = {
      status: historyFilters.status,
      page: historyFilters.page,
      per_page: historyFilters.perPage,
    }

    if (historyFilters.stationId !== null) {
      payload.station_id = historyFilters.stationId
    }

    if (historyFilters.refreshType) {
      payload.refresh_type = historyFilters.refreshType
    }

    if (historyFilters.start) {
      payload.start = historyFilters.start
    }

    if (historyFilters.end) {
      payload.end = historyFilters.end
    }

    if (!historyFilters.start && !historyFilters.end) {
      payload.range = historyFilters.range
    }

    getSuperadminWeatherReports(payload, { signal: controller.signal })
      .then((response) => {
        setHistoryState({
          rows: Array.isArray(response.data) ? response.data : [],
          total: response.paging?.total ?? 0,
          hasNext: Boolean(response.paging?.has_next),
        })
      })
      .catch((error) => {
        if ((error as Error).name === 'AbortError') {
          return
        }
        setHistoryError(getErrorMessage(error, 'Unable to load weather history.'))
      })
      .finally(() => {
        setHistoryLoading(false)
      })

    return () => controller.abort()
  }, [historyFilters, historyReloadKey, isSuperadmin])

  const buildPayload = React.useCallback((): SaveSuperadminWeatherSettingsPayload | null => {
    const specificHours = sanitizeHours(form.specificHours)

    if (form.cronType === 'specific_hours' && specificHours.length === 0) {
      setFormErrors({ specificHours: 'Select at least one hour for the refresh schedule.' })
      return null
    }

    setFormErrors({})

    const manualCooldownMinutes = Math.max(0, Number.parseInt(form.manualCooldown, 10) || 0)
    const intervalMinutes = Math.max(1, Number.parseInt(form.intervalMinutes, 10) || 60)

    const cronMode = {
      type: form.cronType,
      interval_minutes: intervalMinutes,
      specific_hours: specificHours,
    }

    const trimmedKey = form.apiKey.trim()
    let apiKeyPayload: string | null = trimmedKey

    if (form.hasStoredApiKey) {
      if (!form.apiKeyDirty) {
        apiKeyPayload = '__UNCHANGED__'
      } else if (trimmedKey === '') {
        apiKeyPayload = ''
      }
    }

    return {
      api_key: apiKeyPayload,
      manual_cooldown_minutes: manualCooldownMinutes,
      cron_mode: cronMode,
      presenter_manual_refresh_enabled: form.presenterManualRefreshEnabled,
    }
  }, [form])

  const handleSave = async () => {
    if (!stationId) {
      setNotice({ type: 'error', message: 'Select a station to manage global weather settings.' })
      return
    }

    const payload = buildPayload()
    if (!payload) {
      return
    }

    setSaving(true)
    setNotice(null)

    try {
      const response = await saveSuperadminWeatherSettings(stationId, payload)
      applySettings(response)
      setNotice({ type: 'success', message: 'Global weather settings saved.' })
    } catch (error) {
      setNotice({ type: 'error', message: getErrorMessage(error, 'Unable to save global weather settings.') })
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!stationId) {
      setNotice({ type: 'error', message: 'Select a station before testing the weather integration.' })
      return
    }

    setTesting(true)
    setNotice(null)

    try {
      const response = await refreshWeather(stationId)
      let message = response.message?.trim() ?? ''
      let type: NoticeState['type'] = 'info'

      if (response.status === 'success') {
        type = 'success'
        if (message === '') {
          message = 'Manual weather refresh succeeded.'
        }
      } else if (response.status === 'cooldown') {
        type = 'info'
        if (message === '') {
          message = 'Manual weather refresh is on cooldown.'
        }
      } else {
        type = 'error'
        if (message === '') {
          message =
            response.error_code && response.error_code !== ''
              ? `Weather refresh failed: ${response.error_code}`
              : 'Weather refresh failed.'
        }
      }

      setNotice({ type, message })

      try {
        const latest = await getSuperadminWeatherSettings(stationId)
        applySettings(latest)
      } catch (reloadError) {
        if ((import.meta as any)?.env?.DEV) {
          console.error('[RPA] Failed to reload weather settings after test', reloadError)
        }
      }
    } catch (error) {
      setNotice({ type: 'error', message: getErrorMessage(error, 'Unable to trigger weather refresh.') })
    } finally {
      setTesting(false)
    }
  }

  const handleResetCounters = async () => {
    if (!stationId) {
      setNotice({ type: 'error', message: 'Select a station before resetting counters.' })
      return
    }

    setResetting(true)
    setNotice(null)

    try {
      const response = await resetSuperadminWeatherCounters(stationId)
      applySettings(response)
      const didReset = Boolean(response.reset?.rate_limits)
      setNotice({
        type: didReset ? 'success' : 'info',
        message: didReset
          ? 'Rate-limit counters were reset.'
          : 'Rate-limit counters were already clear.',
      })
    } catch (error) {
      setNotice({ type: 'error', message: getErrorMessage(error, 'Unable to reset rate-limit counters.') })
    } finally {
      setResetting(false)
    }
  }

  const handleSpecificHoursChange = (event: SelectChangeEvent<typeof form.specificHours>) => {
    const value = event.target.value
    const hours = Array.isArray(value)
      ? value.map((entry) => Number(entry))
      : typeof value === 'string'
        ? value.split(',').map((entry) => Number(entry))
        : []
    setForm((prev) => ({
      ...prev,
      specificHours: sanitizeHours(hours),
    }))
  }

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    setForm((prev) => ({
      ...prev,
      apiKey: nextValue,
      apiKeyDirty: true,
    }))
  }

  const handleIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      intervalMinutes: event.target.value,
    }))
  }

  const handleManualCooldownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      manualCooldown: event.target.value,
    }))
  }

  const handleHistoryStationChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    const nextStationId = value === '' ? null : Number(value)
    setHistoryFilters((prev) => ({
      ...prev,
      stationId: Number.isNaN(nextStationId as number) ? null : nextStationId,
      page: 1,
    }))
  }

  const handleHistoryStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as 'all' | 'success' | 'error'
    setHistoryFilters((prev) => ({
      ...prev,
      status: value,
      page: 1,
    }))
  }

  const handleHistoryRefreshTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    setHistoryFilters((prev) => ({
      ...prev,
      refreshType: value === '' ? null : value,
      page: 1,
    }))
  }

  const handleHistoryRangePreset = (value: '24h' | '48h' | '7d') => {
    setHistoryFilters((prev) => ({
      ...prev,
      range: value,
      start: '',
      end: '',
      page: 1,
    }))
  }

  const handleHistoryDateChange =
    (field: 'start' | 'end') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = clampDateValue(event.target.value)
      setHistoryFilters((prev) => ({
        ...prev,
        [field]: value,
        page: 1,
      }))
    }

  const handleHistoryPageChange = (_: unknown, newPage: number) => {
    setHistoryFilters((prev) => ({
      ...prev,
      page: newPage + 1,
    }))
  }

  const handleHistoryRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value)
    setHistoryFilters((prev) => ({
      ...prev,
      perPage: value,
      page: 1,
    }))
  }

  const handleHistoryReload = () => {
    setHistoryReloadKey((prev) => prev + 1)
  }

  const handleOpenRawDialog = (reportId: number) => {
    setRawDialog({
      open: true,
      reportId,
      loading: true,
      payload: null,
      error: null,
      copied: false,
    })

    getSuperadminWeatherReportRaw(reportId)
      .then((payload) => {
        setRawDialog((prev) => ({
          ...prev,
          loading: false,
          payload,
          error: null,
        }))
      })
      .catch((error) => {
        let message = getErrorMessage(error, 'Unable to load the raw weather response.')
        if ((error as Error & { status?: number }).status === 413) {
          message =
            'The stored weather response exceeds the inline viewer size. Download the JSON or inspect server logs for full details.'
        }
        setRawDialog((prev) => ({
          ...prev,
          loading: false,
          payload: null,
          error: message,
        }))
      })
  }

  const handleCloseRawDialog = () => {
    setRawDialog({
      open: false,
      reportId: null,
      loading: false,
      payload: null,
      error: null,
      copied: false,
    })
  }

  const rawJsonString = React.useMemo(
    () => (rawDialog.payload ? JSON.stringify(rawDialog.payload, null, 2) : ''),
    [rawDialog.payload],
  )

  const handleCopyRaw = async () => {
    if (!rawJsonString) {
      return
    }

    try {
      await navigator.clipboard.writeText(rawJsonString)
      setRawDialog((prev) => ({ ...prev, copied: true, error: prev.error }))
    } catch (error) {
      setRawDialog((prev) => ({
        ...prev,
        copied: false,
        error: getErrorMessage(error, 'Unable to copy JSON to clipboard.'),
      }))
    }
  }

  React.useEffect(() => {
    if (!rawDialog.copied) {
      return
    }

    const timeout = window.setTimeout(() => {
      setRawDialog((prev) => ({ ...prev, copied: false }))
    }, 2000)

    return () => window.clearTimeout(timeout)
  }, [rawDialog.copied])

  const rateLimitEntries = React.useMemo(() => Object.entries(settings?.rate_limits ?? {}), [settings])
  const failureSummary = settings?.failure_summary
  const recentFailures = failureSummary?.recent_failures ?? []
  const isCustomHistoryRange = historyFilters.start !== '' || historyFilters.end !== ''
  const activeHistoryRange = HISTORY_RANGE_PRESETS.find((preset) => preset.value === historyFilters.range)

  return (
    <Stack spacing={3}>
      {notice ? (
        <Alert
          severity={notice.type === 'success' ? 'success' : notice.type === 'error' ? 'error' : 'info'}
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      ) : null}

      {loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void loadSettings()}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : null}

      {loading ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            minHeight: 240,
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">Loading global weather settings…</Typography>
          </Stack>
        </Paper>
      ) : null}

      {!loading && !stationId ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography color="text.secondary">
            Select a station from the header switcher to manage its global weather configuration.
          </Typography>
        </Paper>
      ) : null}

      {!loading && stationId ? (
        <>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6">OpenWeather API key</Typography>
                <Typography variant="body2" color="text.secondary">
                  Provide the API key shared across stations for fetching weather reports.
                </Typography>
              </Stack>

              <Box>
                <TextField
                  label="API key"
                  type={showApiKey ? 'text' : 'password'}
                  value={form.apiKey}
                  onChange={handleApiKeyChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showApiKey ? 'Hide key' : 'Show key'}>
                          <IconButton onClick={() => setShowApiKey((prev) => !prev)} edge="end">
                            {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    form.hasStoredApiKey && !form.apiKeyDirty
                      ? 'Leave blank to keep the existing key.'
                      : undefined
                  }
                />
                {form.hasStoredApiKey && form.apiKey.trim() === '' ? (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    A key is already stored securely. Enter a new key to replace it or leave blank to keep the current value.
                  </Alert>
                ) : null}
              </Box>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={18} /> : <RefreshIcon />}
                  onClick={handleTest}
                  disabled={testing}
                >
                  {testing ? 'Testing…' : 'Test key'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="h6">Refresh schedule</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Control how often weather data is fetched for this tenant.
                  </Typography>
                </Stack>

                <FormControl>
                  <FormLabel id="global-weather-cron-mode">Schedule mode</FormLabel>
                  <RadioGroup
                    aria-labelledby="global-weather-cron-mode"
                    value={form.cronType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        cronType: event.target.value as FormState['cronType'],
                      }))
                    }
                  >
                    <Stack spacing={2.5}>
                      <Box>
                        <FormControlLabel
                          value="interval_minutes"
                          control={<Radio />}
                          label="Run at a fixed interval"
                        />
                        {form.cronType === 'interval_minutes' ? (
                          <Stack spacing={1} sx={{ pl: { xs: 0, md: 4 }, pt: 1 }}>
                            <TextField
                              label="Interval (minutes)"
                              type="number"
                              value={form.intervalMinutes}
                              onChange={handleIntervalChange}
                              inputProps={{ min: 1 }}
                              sx={{ maxWidth: { xs: '100%', sm: 240 } }}
                              helperText="Weather refresh will run on a repeating timer."
                            />
                          </Stack>
                        ) : null}
                      </Box>
                      <Box>
                        <FormControlLabel
                          value="specific_hours"
                          control={<Radio />}
                          label="Run at specific UTC hours"
                        />
                        {form.cronType === 'specific_hours' ? (
                          <Stack spacing={1} sx={{ pl: { xs: 0, md: 4 }, pt: 1 }}>
                            <Select
                              multiple
                              value={form.specificHours}
                              onChange={handleSpecificHoursChange}
                              renderValue={(selected) => {
                                const hours = sanitizeHours(selected as number[])
                                if (hours.length === 0) {
                                  return 'Select hours'
                                }
                                return hours
                                  .map((hour) => `${hour.toString().padStart(2, '0')}:00`)
                                  .join(', ')
                              }}
                              displayEmpty
                              sx={{ maxWidth: { xs: '100%', sm: 360 } }}
                            >
                              {HOUR_OPTIONS.map((hour) => {
                                const label = `${hour.toString().padStart(2, '0')}:00`
                                return (
                                  <MenuItem key={hour} value={hour}>
                                    {label}
                                  </MenuItem>
                                )
                              })}
                            </Select>
                            {formErrors.specificHours ? (
                              <Typography variant="body2" color="error">
                                {formErrors.specificHours}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Select one or more UTC hours. Weather refresh runs shortly after each selected time.
                              </Typography>
                            )}
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                  <TextField
                    label="Manual refresh cooldown (minutes)"
                    type="number"
                    value={form.manualCooldown}
                    onChange={handleManualCooldownChange}
                    inputProps={{ min: 0 }}
                    helperText="Applies to manual refresh requests. Set to 0 to disable the cooldown."
                    sx={{ maxWidth: { xs: '100%', sm: 320 } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.presenterManualRefreshEnabled}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            presenterManualRefreshEnabled: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Allow presenters to trigger manual refresh"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Rate limits</Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor OpenWeather usage and reset counters when the limits roll over.
                </Typography>
              </Stack>

              <Stack spacing={2}>
                {rateLimitEntries.length === 0 ? (
                  <Typography color="text.secondary">Rate-limit data is not yet available.</Typography>
                ) : (
                  rateLimitEntries.map(([key, limit]) => {
                    const typedLimit = limit as SuperadminWeatherRateLimit
                    const label = rateLimitLabels[key] ?? key
                    const warning = Boolean(typedLimit.is_warning)
                    return (
                      <Paper
                        key={key}
                        variant="outlined"
                        sx={(theme) => ({
                          borderRadius: 2,
                          px: 2.5,
                          py: 2,
                          borderColor: warning ? theme.palette.warning.main : undefined,
                          backgroundColor: warning
                            ? theme.palette.mode === 'dark'
                              ? theme.palette.warning.main + '20'
                              : theme.palette.warning.light + '40'
                            : undefined,
                        })}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {label}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${typedLimit.count} / ${typedLimit.limit}`}
                              color={warning ? 'warning' : 'default'}
                              variant={warning ? 'filled' : 'outlined'}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Resets {typedLimit.resets_at ? `at ${formatDateTime(typedLimit.resets_at)}` : 'soon'}.
                          </Typography>
                          {warning ? (
                            <Stack direction="row" spacing={1} alignItems="center" color="warning.main">
                              <WarningAmberIcon fontSize="small" />
                              <Typography variant="body2">
                                Nearing the limit — threshold {typedLimit.warning_threshold} of {typedLimit.limit}.
                              </Typography>
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    )
                  })
                )}
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={resetting ? <CircularProgress size={18} /> : <RestartAltIcon />}
                  onClick={handleResetCounters}
                  disabled={resetting}
                >
                  {resetting ? 'Resetting…' : 'Reset counters'}
                </Button>
              </Stack>

              <Divider />

              <Stack spacing={0.5}>
                <Typography variant="h6">Recent failures</Typography>
                <Typography variant="body2" color="text.secondary">
                  We track the most recent errors to help diagnose availability issues.
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={`${failureSummary?.consecutive_failures ?? 0} consecutive failures`}
                    color={failureSummary?.is_warning ? 'warning' : 'default'}
                    variant={failureSummary?.is_warning ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Warning threshold: {failureSummary?.threshold ?? 0}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Last success: {formatDateTime(failureSummary?.last_success_at ?? null)}
                </Typography>

                {failureSummary?.is_warning ? (
                  <Alert severity="warning">
                    Weather refreshes are repeatedly failing. Inspect recent errors and verify the API key and station location.
                  </Alert>
                ) : null}

                {recentFailures.length === 0 ? (
                  <Typography color="text.secondary">No recent failures detected.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {recentFailures.map((failure, index) => (
                      <Paper key={`${failure.error_code ?? 'failure'}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={600}>
                              {failure.error_code ?? 'Unknown error'}
                            </Typography>
                            <Chip size="small" variant="outlined" label={failure.refresh_type} />
                          </Stack>
                          {failure.error_message ? (
                            <Typography color="text.secondary">{failure.error_message}</Typography>
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(failure.occurred_at)}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Paper>

          {isSuperadmin ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HistoryIcon color="primary" />
                    <Typography variant="h6">Weather API history</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Inspect historical fetches across stations, filter by status, and review the raw JSON payloads on demand.
                  </Typography>
                  <Button variant="outlined" onClick={handleHistoryReload} disabled={historyLoading} startIcon={<RefreshIcon />}>
                    Reload
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl sx={{ minWidth: { xs: '100%', md: 240 } }}>
                      <FormLabel>Station</FormLabel>
                      <Select value={historyFilters.stationId !== null ? String(historyFilters.stationId) : ''} onChange={handleHistoryStationChange} displayEmpty size="small">
                        <MenuItem value="">All stations</MenuItem>
                        {stations.map((station: { id: number; name: string }) => (
                          <MenuItem key={station.id} value={station.id}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: { xs: '100%', md: 160 } }}>
                      <FormLabel>Status</FormLabel>
                      <Select value={historyFilters.status} onChange={handleHistoryStatusChange} size="small">
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="error">Error</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
                      <FormLabel>Refresh type</FormLabel>
                      <Select value={historyFilters.refreshType ?? ''} onChange={handleHistoryRefreshTypeChange} size="small">
                        {WEATHER_REFRESH_TYPES.map((option) => (
                          <MenuItem key={option.value || 'all'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Date range
                    </Typography>
                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1}>
                      <Stack direction="row" spacing={1}>
                        {HISTORY_RANGE_PRESETS.map((preset) => {
                          const active = !isCustomHistoryRange && historyFilters.range === preset.value
                          return (
                            <Button
                              key={preset.value}
                              variant={active ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => handleHistoryRangePreset(preset.value)}
                            >
                              {preset.label}
                            </Button>
                          )
                        })}
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flex={1}>
                        <TextField
                          label="Start date"
                          type="date"
                          value={clampDateValue(historyFilters.start)}
                          onChange={handleHistoryDateChange('start')}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="End date"
                          type="date"
                          value={clampDateValue(historyFilters.end)}
                          onChange={handleHistoryDateChange('end')}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                    </Stack>
                    {isCustomHistoryRange ? (
                      <Typography variant="caption" color="text.secondary">
                        Custom dates override the preset range.
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Showing {activeHistoryRange?.label ?? 'Recent activity'}.
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                {historyError ? <Alert severity="error">{historyError}</Alert> : null}

                <Box sx={{ position: 'relative' }}>
                  {historyLoading ? (
                    <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
                  ) : null}
                  <TableContainer sx={{ maxHeight: 420, mt: historyLoading ? 3 : 0 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Timestamp (UTC)</TableCell>
                          <TableCell>Station</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Refresh type</TableCell>
                          <TableCell>HTTP</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Error</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyState.rows.length === 0 && !historyLoading ? (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <Typography color="text.secondary" align="center">
                                No weather reports found for the selected filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          historyState.rows.map((row) => {
                            const statusColor =
                              row.status === 'success' ? 'success' : row.status === 'error' ? 'error' : 'default'
                            const errorContent =
                              row.error_message && row.error_message.length > 80
                                ? `${row.error_message.slice(0, 77)}…`
                                : row.error_message ?? '—'
                            return (
                              <TableRow key={row.id} hover>
                                <TableCell>{formatDateTime(row.created_at)}</TableCell>
                                <TableCell>{row.station_name || '—'}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={row.status || 'unknown'}
                                    color={statusColor as 'success' | 'error' | 'default'}
                                    variant={statusColor === 'default' ? 'outlined' : 'filled'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" variant="outlined" label={row.refresh_type || 'auto'} />
                                </TableCell>
                                <TableCell>{row.http_status ?? '—'}</TableCell>
                                <TableCell>{formatDuration(row.duration_ms)}</TableCell>
                                <TableCell>
                                  {row.error_message ? (
                                    <Tooltip title={row.error_message}>
                                      <Typography variant="body2" noWrap>
                                        {errorContent}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Button size="small" onClick={() => handleOpenRawDialog(row.id)}>
                                    View raw JSON
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={historyState.total}
                    page={Math.max(0, historyFilters.page - 1)}
                    onPageChange={handleHistoryPageChange}
                    rowsPerPage={historyFilters.perPage}
                    onRowsPerPageChange={handleHistoryRowsPerPageChange}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Rows per page"
                  />
                </Box>
              </Stack>
            </Paper>
          ) : null}
        </>
      ) : null}

      <Dialog open={rawDialog.open} onClose={handleCloseRawDialog} fullWidth maxWidth="md" aria-labelledby="weather-raw-dialog-title">
        <DialogTitle id="weather-raw-dialog-title">Weather report payload</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 240, bgcolor: 'background.paper' }}>
          {rawDialog.loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
              <CircularProgress />
            </Stack>
          ) : rawDialog.error ? (
            <Alert severity="error">{rawDialog.error}</Alert>
          ) : rawJsonString ? (
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 14,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {rawJsonString}
            </Box>
          ) : (
            <Typography color="text.secondary">Raw payload is not available for this report.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyRaw}
            disabled={!rawJsonString || rawDialog.loading}
          >
            {rawDialog.copied ? 'Copied!' : 'Copy JSON'}
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            component="a"
            href={rawDialog.reportId ? getSuperadminWeatherReportRawUrl(rawDialog.reportId, true) : undefined}
            target="_blank"
            rel="noopener"
            download
            disabled={!rawDialog.reportId}
          >
            Download
          </Button>
          <Button startIcon={<CloseIcon />} onClick={handleCloseRawDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default GlobalWeatherSettingsPage
